import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as httpClient from "../../../util/httpClient";
import { updateState, states } from "../StateManagement";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useButtonText } from "../../commonConstants";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";

interface IProps {
    displayData: any;
    manageState: any;
    registrationState: any;
    setRegistrationState: any;
}

const SecurityQuestions = ({ displayData, manageState, setRegistrationState, registrationState }: IProps) => {
    const { t: tLang, i18n: i18nLang } = useTranslation();
    const { register, handleSubmit, errors, watch } = useForm({
        mode: "onTouched",
        criteriaMode: "all",
    });
    let history = useHistory();
    const isBusinessUser = displayData && displayData.isBusinessUser;
    const query = new URLSearchParams(location.search);
    const LookupType: any = query.get("Type");
    const PSSValue = query.get("GNDNRLL");
    const lookUpTypes = ["WALKUP", "SSSOFFER"];
    const IsWhiteLabeled: any = useSelector((state: ApplicationState) =>
        state.configurationState && state.configurationState.configuration.partnerConfig.isWhiteLabeled
            ? state.configurationState.configuration.partnerConfig.isWhiteLabeled
            : false
    );
    const getInternalQuestionsSelectedMap = () => {
        let internalQuestionsSelectedMap = new Map<number, number>();
        if (
            null != registrationState &&
            null != registrationState.securityInfo &&
            registrationState.securityInfo.item &&
            registrationState.securityInfo.item.length > 0
        ) {
            registrationState.securityInfo.item.forEach((securityQuestionMetadata: any, index: number) =>
                internalQuestionsSelectedMap.set(index, securityQuestionMetadata.Id)
            );
        }

        return internalQuestionsSelectedMap;
    };

    const [state, setState] = useState({
        firstInteractionDone: false,
        securityQuestion: [],
        securityQuestionAnswer: [
            { label: "registration.security.question-1", que: 1 },
            { label: "registration.security.question-2", que: 2 },
            { label: "registration.security.question-3", que: 3 },
        ],
        internalQuestionsSelectedMap: getInternalQuestionsSelectedMap(),
    });

    useEffect(() => {
        const preferredLanguage = sessionStorage.getItem('preferredLanguage');
        let languageId = (preferredLanguage === 'en') ? 1 : ((preferredLanguage === 'fr') ? 2 : 1);
        let reqData = {
            registrationType: "Consumer",
            LanguageId: languageId,
        };
        if (isBusinessUser) {
            reqData.registrationType = "Business";
        }
        window.scrollTo(0, 0);
        httpClient.sendRequest("POST", "web/forgot-password/get-security-questions-by-registrationtype", reqData).then((data: any) => {
            if (data["hasError"]) {
                if (data && data.status) {
                    switch (data.status) {
                        case "INTERNAL_ERROR":
                            history.push("/register/error");
                            break;
                        default:
                            history.push("/register/error");
                            break;
                    }
                } else {
                    history.push("/register/error");
                }
            } else {
                setState({ ...state, securityQuestion: data.securityQuestionListByRegistrationType });
                disableSelectedQuestions(data.securityQuestionListByRegistrationType);
            }
        });
    }, []);

    const setPrevious = () => {
        if (((PSSValue && PSSValue.includes('PSS') && !IsWhiteLabeled) || (lookUpTypes.includes(LookupType.toUpperCase()))) && !displayData.billingRequired) {
            manageState(updateState(states, displayData.billingRequired, 1));
        }
        else {
            manageState(updateState(states, displayData.billingRequired, 2));
        }
    };

    const onSubmit = (data: any) => {
        data.item.forEach((item: any) => {
            item.Id = parseInt(item.Id);
            const selectedQuestion: any = state.securityQuestion.filter((securityQue: any) => item.Id === securityQue.id)[0];
            item.Question = selectedQuestion.value;
        });
        setRegistrationState({ ...registrationState, securityInfo: data });
        manageState(updateState(states, displayData.billingRequired, 4));
    };
    const [isNextOrContinue, isPreviousOrBack] = useButtonText();
    const validation = {
        minLengthCharacter: (val: any) => /^.{12,}$/.test(val),
        containsCapitalLetter: (val: any) => /.*[A-Z].*/.test(val),
        containsSpecialLetter: (val: any) => /[*@!#%&()^~{}$\-_+=\[\]\|;:"'<,>\.\?]+/.test(val),
        containsSmallLetter: (val: any) => /.*[a-z].*/.test(val),
        containsNumber: (val: any) => /\d/.test(val),
    };

    const validationMessage: Record<string, string> = {
        minLengthCharacter: tLang("registration.security.validations.12chars"),
        containsCapitalLetter: tLang("registration.security.validations.1upper"),
        containsSmallLetter: tLang("registration.security.validations.1lower"),
        containsNumber: tLang("registration.security.validations.1num"),
        containsSpecialLetter: tLang("registration.security.validations.1special"),
    };

    const disableSelectedQuestions = (data: any) => {
        let tempSet = new Set<number>(state.internalQuestionsSelectedMap.values());
        data.forEach((question: any) => {
            question.isHidden = tempSet.has(question.id);
        });
        setState({ ...state, securityQuestion: data });
    };

    const disableOption = (key: number, selectedQuestionId: string) => {
        state.internalQuestionsSelectedMap.set(key, parseInt(selectedQuestionId));
        disableSelectedQuestions(state.securityQuestion);
    };

    return (
        <div className="box-content">
            <form id="form-que" onSubmit={handleSubmit(onSubmit)}>
                <div className="row between-sm">
                    <div className="col-xs-12 col-sm-7 col-no-gutter">
                        <p>{tLang("registration.security.message")}</p>
                        <div className="margin-bottom-l"></div>
                        <div role="grid" className="data-grid">
                            <div role="row" className="data-row row">
                                <div role="gridcell" className="col-xs-12 col-sm-4 col-no-gutter">
                                    <div className="data-label" id="label-primary-password">
                                        <label htmlFor="password-label">{tLang("registration.security.password")}
                                            <span className="required" title="Mandatory">
                                                {" "}
                                                *
                                            </span>
                                        </label>
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-12 col-sm-8 col-no-gutter">
                                    <div className="data-value">
                                        <input
                                            className="inputbox"
                                            type="password"
                                            id="password-label"
                                            autoComplete="off"
                                            defaultValue={
                                                registrationState.securityInfo && Object.keys(registrationState.securityInfo).length !== 0
                                                    ? registrationState.securityInfo.password
                                                    : ""
                                            }
                                            name="password"
                                            ref={register({ validate: validation })}
                                            onBlur={(e) => setState({ ...state, firstInteractionDone: true })}
                                            aria-required="true"
                                        />
                                        {Object.keys(validationMessage).map((key, value) => {
                                            let validationResultClass;

                                            if (!state.firstInteractionDone && errors && !errors.password) {
                                                validationResultClass = "validation-default";
                                            }

                                            if (state.firstInteractionDone && errors && !errors.password) {
                                                validationResultClass = "validation-true";
                                            }
                                            if (state.firstInteractionDone && errors && errors.password && errors.password.types) {
                                                validationResultClass = errors.password.types[key] ? "validation-false" : "validation-true";
                                            }
                                            if (!state.firstInteractionDone && errors && errors.password && errors.password.types) {
                                                validationResultClass = errors.password.types[key] ? "validation-false" : "validation-true";
                                            }
                                            return (
                                                <span className={validationResultClass} role="alert">
                                                    <p key={key}>{validationMessage[key]}</p>
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            <div role="row" className="data-row row">
                                <div role="gridcell" className="col-xs-12 col-sm-4 col-no-gutter">
                                    <div className="data-label" id="label-confirm-password">
                                        <label htmlFor="confirmpassword-label">{tLang("registration.security.confirm-password")}
                                            <span className="required" title="Mandatory">
                                                {" "}
                                                *
                                            </span>
                                        </label>
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-12 col-sm-8 col-no-gutter">
                                    <div className="data-value">
                                        <input
                                            id="confirmpassword-label"
                                            type="password"
                                            className={errors.confirmNewPassword ? "status-error inputbox" : "inputbox"}
                                            ref={register({
                                                required: true,
                                                validate: (value) => value === watch("password") || "Confirm password do not match.",
                                            })}
                                            name="confirmNewPassword"
                                            defaultValue={
                                                registrationState.securityInfo && Object.keys(registrationState.securityInfo).length !== 0
                                                    ? registrationState.securityInfo.password
                                                    : ""
                                            }
                                            aria-required="true"                                        
                                        />
                                        {errors.confirmNewPassword && errors.confirmNewPassword.type === "validate" && (
                                            <span className="form-error" role="alert">
                                                <p>{tLang("registration.security.validations.password-not-matched")}</p>
                                            </span>
                                        )}
                                        {errors.confirmNewPassword && errors.confirmNewPassword.type === "required" && (
                                            <span className="form-error" role="alert">
                                                <p>{tLang("registration.security.validations.confirm-password-required")}</p>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="margin-bottom-xl"></div>

                        {state.securityQuestionAnswer.length > 0 &&
                            state.securityQuestionAnswer.map((item, index) => {
                                const answerIndex = "answer" + (index + 1);
                                return (
                                    <div role="grid" className="data-grid" key={index}>
                                        <div role="row" className="data-row row">
                                            <div role="gridcell" className="col-xs-12 col-sm-4 col-no-gutter">
                                                <div className="data-label" id={`label-security-question-${index+1}`}>
                                                    <label htmlFor={`label-security-que-${index}`}>{tLang(item.label)}
                                                        <span className="required" title="Mandatory">
                                                            {" "}
                                                            *
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                            <div role="gridcell" className="col-xs-12 col-sm-8 col-no-gutter">
                                                <div className="data-value">
                                                    <select
                                                        className="inputbox"
                                                        id={`label-security-que-${index}`}
                                                        defaultValue={
                                                            registrationState.securityInfo && Object.keys(registrationState.securityInfo).length !== 0
                                                                ? registrationState.securityInfo.item.length > 0 &&
                                                                  registrationState.securityInfo.item[index].Id.toString()
                                                                : ""
                                                        }
                                                        name={`item[${index}].Id`}
                                                        ref={register({ required: true })}
                                                        onChange={(e) => disableOption(index, e.target.value)}
                                                        aria-required="true"
                                                    >
                                                        <option value="">--- {tLang("registration.security.chooseQuestion")} ---</option>
                                                        {state.securityQuestion.map((val: any) => (
                                                            <option
                                                                key={val.id}
                                                                value={val.id}
                                                                disabled={val.isHidden}
                                                                selected={
                                                                    val.id ===
                                                                    (registrationState.securityInfo && Object.keys(registrationState.securityInfo).length !== 0
                                                                        ? registrationState.securityInfo.item.length > 0 &&
                                                                          registrationState.securityInfo.item[index].Id.toString()
                                                                        : "")
                                                                }
                                                            >
                                                                {val.text}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    {errors &&
                                                        errors["item"] &&
                                                        errors["item"][index] &&
                                                        errors["item"][index]["Id"] &&
                                                        errors["item"][index]["Id"].types &&
                                                        errors["item"][index]["Id"].types.required === true && (
                                                        <span className="form-error" role="alert">
                                                                <p>{tLang("registration.security.validations.question-not-selected")}</p>
                                                            </span>
                                                        )}
                                                </div>
                                            </div>
                                        </div>
                                        <div role="row" className="data-row row">
                                            <div role="gridcell" className="col-xs-12 col-sm-4 col-no-gutter">
                                                <div className="data-label" id={`label-security-answer-${index + 1}`}>
                                                    <label htmlFor={`email-${index}`}>{tLang("registration.security.answer")}{" ".repeat(index)}
                                                        <span className="required" title="Mandatory">
                                                            {" "}
                                                            *
                                                        </span>
                                                    </label>                      
                                                </div>
                                            </div>
                                            <div role="gridcell" className="col-xs-12 col-sm-8 col-no-gutter">
                                                <div className="data-value">
                                                    <input
                                                        type="text"
                                                        id={`email-${index}`}
                                                        className={errors[answerIndex] ? "status-error inputbox" : "inputbox"}
                                                        name={`item[${index}].Answer`}
                                                        ref={register({
                                                            required: true,
                                                        })}
                                                        defaultValue={
                                                            registrationState.securityInfo && Object.keys(registrationState.securityInfo).length !== 0
                                                                ? registrationState.securityInfo.item.length > 0 &&
                                                                  registrationState.securityInfo.item[index].Answer
                                                                : ""
                                                        }
                                                        aria-required="true"
                                                    />
                                                    {errors["item"] &&
                                                        errors["item"][index] &&
                                                        errors["item"][index]["Answer"] &&
                                                        errors["item"][index]["Answer"].types &&
                                                        errors["item"][index]["Answer"].types.required === true && (
                                                        <span className="form-error" role="alert">
                                                                <p>{tLang("registration.security.validations.answer-required")}</p>
                                                            </span>
                                                        )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="margin-bottom-xs"></div>
                                    </div>
                                );
                            })}
                    </div>
                </div>
                <div className="row between-xs">
                    <div className="col-xs col-no-gutter flex-grow-null"></div>
                    <div className="col-xs col-no-gutter flex-grow-null">
                        <ul className="list-inline list-reset">
                            <li className="margin-left-s">
                                <button
                                    type="button"
                                    className="btn-default
                                            btn-default btn-min-width"
                                    onClick={() => setPrevious()}
                                >
                                    <span className="btn-text">{isPreviousOrBack}</span>
                                </button>
                            </li>
                            <li className="margin-left-s">
                                <button
                                    onClick={() => window.addToDataLayer({ event: "Submit Password and Security Questions" })}
                                    type="submit"
                                    className="btn-default btn-primary btn-min-width"
                                >
                                    <span className="btn-text">{isNextOrContinue}</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default SecurityQuestions;
